import { Typography, Box, Container, Grid, Card } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { UserActivityTypes } from "services/Interfaces";
import { ReimbursementRequest, useWhenBenefitsQuery } from "services/GraphQl";
import { useAuth } from "contexts/User";
import { addDoc, collection } from "firebase/firestore";
import { useFirebase } from "contexts/Firebase";
import { doc, getDoc } from "firebase/firestore";
import BankAccountModal from "components/WhenBenefit/BankAccountModal";
import ReimbursementRequestTable from "./components/ReimbursementRequestTable";
import MonthlyBenefitSummary from "./components/MonthlyBenefitSummary";
import LumpSumBenefitSummary from "./components/LumpSumBenefitSummary";
import ViewReimbursementModal from "./components/ViewReimbursementModal";

function WhenBenefitPage() {
  const { firestore } = useFirebase();
  const { user } = useAuth();
  const [viewReimbursement, setViewReimbursement] = useState<ReimbursementRequest | undefined>(undefined);
  const [openViewReimbursement, setOpenViewReimbursement] = useState(false);
  const [hasBankAccount, setHasBankAccount] = useState(false);
  const [bankAccountModalOpen, setBankAccountModalOpen] = useState(false);

  const {
    data: whenBenefitsData,
    loading: whenBenefitsLoading,
  } = useWhenBenefitsQuery({
    variables: {
      uid: user?.uid,
    },
    skip: !user,
  });

  const addActivity = useCallback(async () => {
    try {
      const userActivityCollectionRef = collection(firestore, `users/${user?.uid}/activity`);
      await addDoc(userActivityCollectionRef, {
        activity: UserActivityTypes.VIEWEDWHENBENEFIT,
        type: "user",
        userId: user?.uid,
        timestamp: new Date(),
      });
    } catch (error) {
      console.log("error", error);
    }
  }, [firestore, user?.uid]);

  useEffect(() => {
    addActivity();
  }, [addActivity]);

  useEffect(() => {
    const checkBankAccount = async () => {
      if (user) {
        try {
          const docRef = doc(firestore, "users", user.uid, "settings", "directDeposit");
          const docSnapshot = await getDoc(docRef);
          setHasBankAccount(docSnapshot.exists());
        } catch (error) {
          console.error("Error checking directDeposit:", error);
          setHasBankAccount(false);
        }
      }
    };
    checkBankAccount();
  }, [user, firestore]);

  if (whenBenefitsLoading) {
    return null;
  }

  if (!whenBenefitsData?.whenBenefits?.length) {
    return (
      <Box>
        <Typography variant="h2" sx={{ p: 2 }}>
          User has no case when benefit
        </Typography>
      </Box>
    );
  }

  // used non null assertion to avoid typescript error, check above should ensure this is not null
  const caseId = whenBenefitsData?.whenBenefits[0]?.caseId!;
  const companyId = whenBenefitsData?.whenBenefits[0]?.companyId!;
  const activeWhenBenefit = whenBenefitsData?.whenBenefits[0]?.benefit!;

  const benefitAllocationType = activeWhenBenefit?.distributionType;
  const BenefitSummaryComponent =
    benefitAllocationType === "MONTHLY_ALLOCATION"
      ? MonthlyBenefitSummary
      : benefitAllocationType === "LUMP_SUM"
      ? LumpSumBenefitSummary
      : null;

  return (
    <Box>
      <Box
        sx={{
          py: 4,
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12}>
              <Card sx={{ width: "100%" }}>
                <Box>
                  <Card
                    key={activeWhenBenefit?.__typename}
                    sx={{
                      p: 0,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "primary.main",
                        width: "100%",
                        p: 3,
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        mb: 3,
                      }}
                    >
                      <Typography variant="h2" gutterBottom color="#ffffff" sx={{ fontWeight: "bold" }}>
                        Your When Benefit
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 3 }} color="#ffffff">
                        This benefit was provided by {companyId} as part of your severance package. You are able to use
                        your When Benefit to get reimbursed for the premiums for the health insurance policy you select.
                        All you need to do is submit a claim with proof of payment for any health insurance policy
                        you&apos;ve selected.
                      </Typography>
                    </Box>

                    {BenefitSummaryComponent && (
                      <BenefitSummaryComponent
                        whenBenefit={activeWhenBenefit}
                        hasBankAccount={hasBankAccount}
                        setBankAccountModalOpen={setBankAccountModalOpen}
                        companyId={companyId}
                        caseId={caseId}
                      />
                    )}
                  </Card>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <ReimbursementRequestTable
                caseWhenBenefit={activeWhenBenefit}
                onViewReimbursement={(reimbursement: ReimbursementRequest) => {
                  setViewReimbursement(reimbursement);
                  setOpenViewReimbursement(true);
                }}
              />
              {openViewReimbursement && viewReimbursement && (
                <ViewReimbursementModal
                  openViewReimbursement={openViewReimbursement}
                  setOpenViewReimbursement={setOpenViewReimbursement}
                  reimbursement={viewReimbursement}
                />
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>
      <BankAccountModal
        headerText="Add your bank account to get paid back faster"
        bodyText=" We've received your request and are reviewing it for reimbursement. In order to receive your reimbursement funds more quickly, add your bank account so When can pay you electronically."
        submitButtonText="Save"
        cancelButtonText="Skip for now"
        open={bankAccountModalOpen}
        onClose={() => {
          setBankAccountModalOpen(false);
        }}
      />
    </Box>
  );
}
export default WhenBenefitPage;
